// SYNC WITH THE CLOUD (owner 2026-10-24: "a button to sync model from the cloud and asks
// if we want upload the file or download the file").
//
// The server compares every model file on every destination that has a models folder
// with ours (GET /project-models/:id/sync-plan) and says which side moved since the last
// sync. Each row starts on its suggestion - download what moved there, upload what moved
// here - and a file changed on BOTH sides starts on Skip, so nothing is overwritten
// without a choice. "Sync" sends what is chosen (POST .../sync-apply); downloads become
// new versions of the same model, uploads the next version of the same file there.
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Radio, Spin, Table, Tag, Tooltip } from "antd";
import addNotification from "helpers/notify";
import { modelSyncPlan, applyModelSync } from "api/projectModels";
import { openFolderPaths } from "components/Integrations/FolderPathsPanel";

const STATE = {
  "remote-only": { label: "Only there", color: "blue", why: "In the cloud folder, not here yet" },
  "local-only": { label: "Only here", color: "purple", why: "Here, not in this cloud folder yet" },
  "remote-newer": { label: "Newer there", color: "blue", why: "Changed in the cloud since the last sync" },
  "local-newer": { label: "Newer here", color: "purple", why: "Changed here since the last sync" },
  "both-changed": { label: "Changed on both", color: "gold", why: "Changed in the cloud AND here since the last sync - choose which one wins" },
  same: { label: "Up to date", color: "green", why: "The same version on both sides" },
};

const ver = (n) => (n ? `v${n}` : "");

export default function ModelSyncModal({ open, projectId, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  const [showSame, setShowSame] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let live = true;
    setPlan(null);
    setError("");
    modelSyncPlan(projectId)
      .then((p) => {
        if (!live) return;
        setPlan(p);
        setChoice(Object.fromEntries((p.items || []).map((x) => [x.key, x.suggested])));
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [open, projectId]);

  const items = useMemo(() => ((plan && plan.items) || []).filter((x) => showSame || x.state !== "same"), [plan, showSame]);
  const chosen = ((plan && plan.items) || []).filter((x) => choice[x.key] === "download" || choice[x.key] === "upload");
  const downloads = chosen.filter((x) => choice[x.key] === "download").length;
  const uploads = chosen.length - downloads;

  const go = async () => {
    setBusy(true);
    try {
      const out = await applyModelSync(projectId, chosen.map((x) => ({
        destination: x.destination, action: choice[x.key], id: x.id || undefined, fileId: x.remote ? x.remote.fileId : undefined,
      })));
      const ok = out.filter((o) => o.queued).length;
      const not = out.filter((o) => o.skipped);
      if (ok) addNotification(`${ok} on the way: downloads become new versions here, uploads the next version there`, "success");
      not.slice(0, 5).forEach((o) => addNotification(`${o.name || "A file"}: ${o.skipped}`, "warning"));
      if (!ok && !not.length) addNotification("Nothing was sent", "warning");
      // the page offers "Export to project" for the downloads once they are in
      onDone && onDone(out);
      onClose();
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { title: "File", dataIndex: "name", render: (n) => <b>{n}</b> },
    { title: "Destination", dataIndex: "destinationName" },
    {
      title: "Here",
      key: "local",
      width: 80,
      render: (x, r) => (r.local ? ver(r.local.version) : <span className="text-muted">-</span>),
    },
    {
      title: "There",
      key: "remote",
      width: 80,
      render: (x, r) => (r.remote ? <Tooltip title={r.remote.path}>{ver(r.remote.version) || "yes"}</Tooltip> : <span className="text-muted">-</span>),
    },
    {
      title: "Status",
      dataIndex: "state",
      width: 150,
      render: (s, r) => (
        <Tooltip title={r.refused || (STATE[s] || {}).why}>
          <Tag color={(STATE[s] || {}).color}>{(STATE[s] || {}).label || s}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Do",
      key: "do",
      width: 260,
      render: (x, r) => (
        <Radio.Group
          size="small"
          value={choice[r.key]}
          disabled={!!r.refused}
          onChange={(e) => setChoice((c) => ({ ...c, [r.key]: e.target.value }))}
          optionType="button"
          data-testid={`sync-do-${r.key}`}
          options={[
            { value: "download", label: "Download", disabled: !r.remote },
            { value: "upload", label: "Upload", disabled: !r.local || r.local.status !== "stored" },
            { value: "skip", label: "Skip" },
          ]}
        />
      ),
    },
  ];

  let body;
  if (error) body = <p className="text-danger">{error}</p>;
  else if (!plan) body = <Spin />;
  else if (!plan.destinations.length || plan.needsPath) {
    // NO MODELS FOLDER: said, with the way to set one (owner 2026-10-25)
    body = (
      <div data-testid="model-sync-needs-path">
        <p className="text-muted">
          {plan.message || "This project has no ACC or Procore models folder yet."}
        </p>
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={() => openFolderPaths(projectId)}>
          Set the models download / upload folders
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {(plan.problems || []).map((p) => (
          <p key={p.destination} className="text-danger mb-1">{p.name}: {p.error}</p>
        ))}
        <label className="d-inline-flex align-items-center mb-2" style={{ gap: 6 }}>
          <input type="checkbox" checked={showSame} onChange={(e) => setShowSame(e.target.checked)} /> Show files that are up to date
        </label>
        <Table rowKey="key" size="small" dataSource={items} columns={columns} pagination={{ pageSize: 50 }}
          locale={{ emptyText: "Everything is up to date on every destination." }} />
      </>
    );
  }

  return (
    <Modal
      open={open}
      title="Sync model files with ACC / Procore"
      width={980}
      onCancel={() => { if (!busy) onClose(); }}
      okText={chosen.length ? `Sync ${[downloads ? `${downloads} download${downloads === 1 ? "" : "s"}` : "", uploads ? `${uploads} upload${uploads === 1 ? "" : "s"}` : ""].filter(Boolean).join(" and ")}` : "Sync"}
      okButtonProps={{ disabled: !chosen.length, loading: busy, "data-testid": "sync-go" }}
      onOk={go}
      destroyOnClose
    >
      {body}
    </Modal>
  );
}
